import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  Existencia, FichaRefaccion, MovimientoInventario, Opcion, Pagina, Refaccion,
  ResumenAlmacen, Sucursal, SurtidoDeLaTienda,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import {
  Cargando, Dialogo, Error, Falla, Faltantes, motivoDeFalla, Paginacion,
} from '../componentes/comunes';
import { BuscadorRefaccion } from '../componentes/BuscadorRefaccion';
import SolicitudesAlmacen from './SolicitudesAlmacen';
import PorRecibir from './PorRecibir';
import { CotizacionesPorAutorizar } from './Ordenes';

/**
 * Almacén: inventario y movimientos.
 *
 * Hoy hay una bodega central. El selector aparece sólo cuando hay más de
 * una, para no pedir una decisión que no existe.
 */
export default function Almacen() {
  const { puede } = useSesion();

  // Las solicitudes van primero porque son lo que a almacén le llega de
  // fuera: inventario y movimientos son consulta, esto es trabajo.
  const [vista, setVista] = useState<
    'cotizaciones' | 'solicitudes' | 'por_recibir' | 'existencias' | 'movimientos'
    | 'surtido'
  >(puede('almacen', 'ver') ? 'solicitudes' : 'surtido');
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [bodegaId, setBodegaId] = useState('');
  const [resumen, setResumen] = useState<ResumenAlmacen | null>(null);
  const [operacion, setOperacion] = useState<'entrada' | 'salida' | 'ajuste' | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [fallaBodegas, setFallaBodegas] = useState<string>();

  /**
   * Y quien sólo trae el permiso del surtido no pide lo del almacén.
   *
   * La API se lo negaría —con razón— y la pantalla arrancaría con dos
   * errores que no vienen al caso: ni las bodegas ni el resumen tienen
   * nada que ver con la pestaña que sí puede ver.
   */
  const verAlmacen = puede('almacen', 'ver');

  useEffect(() => {
    if (!verAlmacen) return;

    api.get<Opcion[]>('/catalogos/bodegas/opciones')
      .then((lista) => {
        setBodegas(lista);
        setFallaBodegas(undefined);
        if (lista.length === 1) setBodegaId(lista[0].id);
      })
      .catch((e: unknown) => {
        setBodegas([]);
        setFallaBodegas(motivoDeFalla(e));
      });
  }, [verAlmacen]);

  useEffect(() => {
    if (!verAlmacen) return;

    api.get<ResumenAlmacen>('/almacen/resumen', { bodegaId: bodegaId || undefined })
      .then(setResumen)
      .catch(() => undefined);
  }, [bodegaId, recarga, verAlmacen]);

  // Firmar las refacciones de una cotización tiene permiso propio, no es
  // el de almacén: son cosas distintas y la 0036 las separó.
  const firmaRefacciones = puede('cotizacion_refacciones', 'autorizar');
  const puedeMover = puede('almacen', 'crear');
  const puedeAjustar = puede('almacen', 'editar');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Almacén</h1>
          <p className="sub">
            Qué hay, cuánto vale y por qué cambió. Cada movimiento deja su
            saldo en el historial, así que el inventario siempre se puede
            explicar.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {puedeMover && (
            <>
              <button className="primario" onClick={() => setOperacion('entrada')}>
                Registrar entrada
              </button>
              <button onClick={() => setOperacion('salida')}>Registrar salida</button>
            </>
          )}
          {puedeAjustar && (
            <button onClick={() => setOperacion('ajuste')}>Ajustar por conteo</button>
          )}
        </div>
      </div>

      {resumen && (
        <div className="rejilla-catalogos">
          <Tarjeta titulo="Referencias con existencia" valor={`${resumen.conStock}`}
                   pie={`de ${resumen.referencias} en catálogo`} />
          <Tarjeta titulo="Piezas en almacén" valor={resumen.piezas.toLocaleString('es-MX')} />
          <Tarjeta titulo="Valor del inventario" valor={moneda(resumen.valor)}
                   pie="a costo promedio" />
          <Tarjeta titulo="Por reponer" valor={`${resumen.porReponer}`}
                   pie="bajo su mínimo" alerta={resumen.porReponer > 0} />
        </div>
      )}

      <nav className="submenu">
        {/* Las refacciones de una cotización las firma almacén: el precio
            de una pieza sale del inventario, que es lo que hay en esta
            pantalla. Antes se podía hacer desde la ficha de la orden,
            pero sin lista no había cómo saber que algo estaba
            esperando. */}
        {firmaRefacciones && (
          <button
            className={vista === 'cotizaciones' ? 'activo' : ''}
            onClick={() => setVista('cotizaciones')}
          >
            Cotizaciones por autorizar
          </button>
        )}
        {/* Las cuatro de almacén van juntas detrás de su permiso: quien
            entra sólo por el surtido no tiene nada que hacer en ellas, y
            enseñarlas sería ofrecer cuatro pantallas que la API le va a
            negar. */}
        {verAlmacen && (
        <>
        <button
          className={vista === 'solicitudes' ? 'activo' : ''}
          onClick={() => setVista('solicitudes')}
        >
          Solicitudes
        </button>
        <button
          className={vista === 'por_recibir' ? 'activo' : ''}
          onClick={() => setVista('por_recibir')}
        >
          Por recibir
        </button>
        <button
          className={vista === 'existencias' ? 'activo' : ''}
          onClick={() => setVista('existencias')}
        >
          Inventario
        </button>
        <button
          className={vista === 'movimientos' ? 'activo' : ''}
          onClick={() => setVista('movimientos')}
        >
          Movimientos
        </button>
        </>
        )}
        {/* Qué se le ha surtido a cada tienda.
            Karin: «es importante para soporte tener la manera de entrar y
            ver el histórico […] para no caer en que soliciten una manguera
            3 dias despues de haberlo hecho». El dato existía repartido en
            tres tablas que no se hablan; aquí se lee junto. */}
        {puede('surtido_tienda', 'ver') && (
          <button
            className={vista === 'surtido' ? 'activo' : ''}
            onClick={() => setVista('surtido')}
          >
            Surtido por tienda
          </button>
        )}
        {/* El selector sólo donde hay algo que filtrar por bodega: el
            inventario y sus movimientos. En las bandejas de trabajo no
            pinta nada. */}
        {verAlmacen && bodegas.length > 1 && (vista === 'existencias' || vista === 'movimientos') && (
          <select
            value={bodegaId}
            onChange={(e) => setBodegaId(e.target.value)}
            style={{ marginLeft: 'auto' }}
          >
            <option value="">Todas las bodegas</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        )}
      </nav>

      {vista === 'cotizaciones' && <CotizacionesPorAutorizar />}
      {vista === 'solicitudes' && <SolicitudesAlmacen />}
      {vista === 'por_recibir' && <PorRecibir />}
      {vista === 'existencias' && <Existencias bodegaId={bodegaId} recarga={recarga} />}
      {vista === 'movimientos' && <Movimientos bodegaId={bodegaId} recarga={recarga} />}
      {vista === 'surtido' && <SurtidoPorTienda />}

      {operacion && (
        <FormularioMovimiento
          operacion={operacion}
          bodegas={bodegas}
          fallaBodegas={fallaBodegas}
          bodegaPreferida={bodegaId}
          onCerrar={() => setOperacion(null)}
          onHecho={() => { setOperacion(null); setRecarga((n) => n + 1); }}
        />
      )}
    </>
  );
}

function Tarjeta({
  titulo, valor, pie, alerta,
}: {
  titulo: string; valor: string; pie?: string; alerta?: boolean;
}) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
      {pie && <div className="conteo">{pie}</div>}
    </div>
  );
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

// ═══════════════════════════════════════════════════════════ inventario

/**
 * El inventario: qué hay, cuánto y cuánto vale.
 *
 * La pestaña se llama Inventario porque es como se le dice en la
 * operación. El componente y la ruta de la API siguen diciendo
 * «existencias», que es lo que la columna mide; renombrar eso obligaría a
 * tocar la API y las pruebas sin que nadie lo note en pantalla.
 */
function Existencias({ bodegaId, recarga }: { bodegaId: string; recarga: number }) {
  const [busqueda, setBusqueda] = useState('');
  const [situacion, setSituacion] = useState('');
  const [areaId, setAreaId] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<Existencia> | null>(null);
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [ficha, setFicha] = useState<Existencia | null>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Existencia>>('/almacen/existencias', {
      busqueda: busqueda || undefined,
      bodegaId: bodegaId || undefined,
      areaId: areaId || undefined,
      situacion: situacion || undefined,
      pagina: numero,
      limite: 50,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, bodegaId, areaId, situacion, numero, recarga]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <div className="panel">
      <div className="controles">
        <input
          type="search"
          placeholder="Código, número de parte o nombre…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        <select value={areaId} onChange={(e) => { setAreaId(e.target.value); setNumero(1); }}>
          <option value="">Todas las áreas</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <select value={situacion} onChange={(e) => { setSituacion(e.target.value); setNumero(1); }}>
          <option value="">Todo el catálogo</option>
          <option value="con_stock">Con existencia</option>
          <option value="sin_stock">Sin existencia</option>
          <option value="bajo_minimo">Bajo mínimo</option>
        </select>
      </div>

      <Error error={error} />

      {situacion === 'bajo_minimo' && (
        <div className="aviso" style={{ marginBottom: 12 }}>
          El filtro se aplica sobre lo disponible: existencia menos lo
          reservado.
        </div>
      )}

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay refacciones que cumplan esos filtros.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  {/* El número de parte es con el que se cotiza y se pide
                      al proveedor: quien busca una pieza para comprarla
                      busca por ése, no por el código interno. */}
                  <th>Nº de parte</th>
                  <th>Refacción</th>
                  <th>Área</th>
                  <th className="numero">Existencia</th>
                  <th className="numero">Reservado</th>
                  <th className="numero">Disponible</th>
                  <th className="numero">Mínimo</th>
                  <th className="numero">Costo</th>
                  <th className="numero">Valor</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((e) => (
                  <tr key={e.refaccion.id}>
                    <td className="mono">{e.refaccion.codigo}</td>
                    <td className="mono">{e.refaccion.numeroParte ?? '—'}</td>
                    <td>
                      <button className="enlace" onClick={() => setFicha(e)}>
                        {e.refaccion.nombre}
                      </button>
                      {/* Con una sola bodega el desglose no dice nada que
                          no diga ya el total. Con varias, es la diferencia
                          entre saber que hay piezas y saber dónde están.

                          La que está bajo su mínimo va en rojo: es lo que
                          el total esconde —cien en Bajío y cero en Mérida
                          suman bien— y es el renglón que hay que reponer. */}
                      {e.bodegas.length > 1 && (
                        <div className="conteo">
                          {e.bodegas.map((b, i) => (
                            <span key={b.id}>
                              {i > 0 && ' · '}
                              <span className={b.bajoMinimo ? 'etiqueta no' : ''}>
                                {b.nombre}: {b.cantidad}
                                {b.minimo > 0 && ` / ${b.minimo}`}
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{e.refaccion.area?.nombre ?? '—'}</td>
                    <td className="numero">{e.cantidad}</td>
                    <td className="numero">{e.reservada || '—'}</td>
                    <td className="numero">
                      {e.bajoMinimo
                        ? <span className="etiqueta no">{e.disponible}</span>
                        : e.disponible}
                    </td>
                    <td className="numero">{e.stockMinimo}</td>
                    <td className="numero">{moneda(e.costoPromedio)}</td>
                    <td className="numero">{moneda(e.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina}
            limite={pagina.limite}
            total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {ficha && (
        <FichaDeRefaccion existencia={ficha} onCerrar={() => setFicha(null)} />
      )}
    </div>
  );
}

/**
 * La ficha de una refacción, tal como se abre desde el inventario.
 *
 * Lo que se pregunta al abrir una pieza no es su historial —para eso está
 * la pestaña de Movimientos— sino qué es y de dónde sale: para qué equipo
 * sirve, cómo se ve en el despiece, y a quién se le compra.
 *
 * Los movimientos siguen a un clic, plegados. Estaban de más ocupando la
 * pantalla, pero quitarlos del todo obligaría a irse a otra pestaña y
 * filtrar a mano para responder «¿por qué el saldo es éste?».
 */
/**
 * A partir de cuántas piezas se avisa que faltan, en cada bodega.
 *
 * El mínimo del conjunto —el de la refacción— sigue existiendo y dice
 * que la pieza escasea en toda la operación. Éste dice que falta *donde
 * hace falta*, que es lo que no se podía ver con una sola bodega: cien
 * piezas en Bajío y cero en Mérida suman bien y dejan a Mérida sin nada.
 *
 * Se guarda al salir del campo y no con un botón: son ocho renglones y
 * un botón por renglón sería ocho botones; uno solo obligaría a
 * acordarse de pulsarlo después de teclear.
 */
function MinimosPorBodega({
  refaccionId, bodegas, onError,
}: {
  refaccionId: string;
  bodegas: Existencia['bodegas'];
  onError: (e: unknown) => void;
}) {
  const { puede } = useSesion();
  const [minimos, setMinimos] = useState<Record<string, string>>(
    () => Object.fromEntries(bodegas.map((b) => [b.id, String(b.minimo)])),
  );
  const [guardado, setGuardado] = useState<string | null>(null);

  if (!puede('almacen', 'editar')) return null;

  async function guardar(bodegaId: string, original: number) {
    const valor = Number(minimos[bodegaId] ?? 0);

    // No se manda si no cambió: salir de un campo que sólo se miró no
    // debería escribir en la bitácora.
    if (!Number.isFinite(valor) || valor < 0 || valor === original) return;

    try {
      await api.post('/almacen/minimos', { refaccionId, bodegaId, minimo: valor });
      setGuardado(bodegaId);
    } catch (e) {
      onError(e);
    }
  }

  return (
    <>
      <h3>Cuánto debe haber en cada bodega</h3>
      <table>
        <thead>
          <tr>
            <th>Bodega</th>
            <th className="numero">Hay</th>
            <th className="numero">Mínimo</th>
          </tr>
        </thead>
        <tbody>
          {bodegas.map((b) => (
            <tr key={b.id}>
              <td>
                {b.nombre}
                {b.bajoMinimo && <span className="etiqueta no"> bajo mínimo</span>}
                {guardado === b.id && <span className="ayuda"> · guardado</span>}
              </td>
              <td className="numero">{b.cantidad}</td>
              <td className="numero">
                <input
                  type="number" min={0} style={{ width: 80 }}
                  value={minimos[b.id] ?? ''}
                  onChange={(e) => {
                    setGuardado(null);
                    setMinimos((antes) => ({ ...antes, [b.id]: e.target.value }));
                  }}
                  onBlur={() => { void guardar(b.id, b.minimo); }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="ayuda">
        {/* Cero no es «no debe haber»: es «no me avises». La diferencia
            importa porque todas nacen en cero. */}
        Cero es «no se vigila». Se guarda al salir del campo.
      </span>
    </>
  );
}

function FichaDeRefaccion({
  existencia, onCerrar,
}: {
  existencia: Existencia;
  onCerrar: () => void;
}) {
  const [ficha, setFicha] = useState<FichaRefaccion | null>(null);
  const [movimientos, setMovimientos] = useState<MovimientoInventario[] | null>(null);
  const [verMovimientos, setVerMovimientos] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refaccionId = existencia.refaccion.id;

  useEffect(() => {
    api.get<FichaRefaccion>(`/refacciones/${refaccionId}`)
      .then(setFicha)
      .catch(setError);
  }, [refaccionId]);

  // Los movimientos se piden sólo si alguien los abre: son cien renglones
  // que casi nadie va a mirar.
  useEffect(() => {
    if (!verMovimientos || movimientos) return;

    api.get<Pagina<MovimientoInventario>>('/almacen/movimientos', {
      refaccionId, limite: 100,
    })
      .then((r) => setMovimientos(r.data))
      .catch(setError);
  }, [verMovimientos, movimientos, refaccionId]);

  return (
    <Dialogo
      titulo={`${existencia.refaccion.codigo} — ${existencia.refaccion.nombre}`}
      onCerrar={onCerrar}
      ancho
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />

      <div className="controles">
        <span>Existencia: <strong>{existencia.cantidad}</strong></span>
        <span>Reservado: <strong>{existencia.reservada}</strong></span>
        <span>Disponible: <strong>{existencia.disponible}</strong></span>
        <span>Costo promedio: <strong>{moneda(existencia.costoPromedio)}</strong></span>
      </div>

      {/*
        Cuánto debe haber en cada bodega.

        Va en la ficha y no en el listado porque es una decisión por
        pieza y por plaza: se piensa mirando esta refacción, no
        recorriendo la tabla. Nacen todos en cero —la 0055 no reparte el
        mínimo global entre ocho bodegas, sería inventar números— así que
        hasta que se capturen, ninguna alerta salta.
      */}
      {existencia.bodegas.length > 0 && (
        <MinimosPorBodega
          refaccionId={refaccionId}
          bodegas={existencia.bodegas}
          onError={setError}
        />
      )}

      {!ficha ? <Cargando /> : (
        <>
          <h3>La pieza</h3>
          <div className="rejilla-datos">
            <Dato etiqueta="Código" valor={ficha.codigo} />
            <Dato etiqueta="Nº de parte" valor={ficha.numeroParte} />
            <Dato etiqueta="Área" valor={ficha.area?.nombre ?? null} />
            <Dato etiqueta="Unidad" valor={ficha.unidadMedida} />
            <Dato etiqueta="Mínimo" valor={String(ficha.stockMinimo)} />
            {ficha.descripcion && (
              <div className="campo ancho-total">
                <label>Descripción</label>
                <span>{ficha.descripcion}</span>
              </div>
            )}
          </div>

          <h3>En qué equipo va</h3>
          {ficha.compatibilidades.length === 0 ? (
            <div className="vacio">
              Nadie ha registrado para qué equipo sirve esta pieza. Se agrega
              en Administración → Refacciones.
            </div>
          ) : (
            <table>
              <thead>
                <tr><th>Equipo</th><th>Marca</th><th>Modelo</th></tr>
              </thead>
              <tbody>
                {ficha.compatibilidades.map((c) => (
                  <tr key={c.id}>
                    <td>{c.tipoEquipo?.nombre ?? '—'}</td>
                    <td>{c.marca?.nombre ?? '—'}</td>
                    {/* Sin modelo, la compatibilidad vale para todos los de
                        esa marca: es más amplia, no menos precisa. */}
                    <td>{c.modelo?.nombre ?? 'todos'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Despiece</h3>
          {ficha.despiece ? (
            <p>
              <button
                className="enlace"
                onClick={() => {
                  void api.descargar(
                    `/refacciones/${ficha.id}/despiece`, undefined, ficha.despiece?.nombre,
                  );
                }}
              >
                {ficha.despiece.nombre}
              </button>
              <span className="conteo"> · subido el {fechaCorta(ficha.despiece.subidoEn)}</span>
            </p>
          ) : (
            <div className="vacio">
              Sin despiece. Es el plano que dice qué pieza es y dónde va;
              se sube en Administración → Refacciones.
            </div>
          )}

          <h3>A quién se le compra</h3>
          {ficha.proveedores.length === 0 ? (
            <div className="vacio">
              Todavía no se le ha comprado a nadie. Esta lista sale de las
              órdenes de compra emitidas, no de un catálogo: dice a quién se
              le compró de verdad.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Contacto</th>
                  <th className="numero">Compras</th>
                  <th className="numero">Piezas</th>
                  <th>Última</th>
                  <th className="numero">Último precio</th>
                </tr>
              </thead>
              <tbody>
                {ficha.proveedores.map((p) => (
                  <tr key={p.id}>
                    <td>{p.nombre}</td>
                    <td className="conteo">{p.telefono ?? p.correo ?? '—'}</td>
                    <td className="numero">{p.compras}</td>
                    <td className="numero">{p.piezas}</td>
                    <td>
                      {p.ultimaFecha ? fechaCorta(p.ultimaFecha) : '—'}
                      <div className="conteo mono">{p.ultimoFolio}</div>
                    </td>
                    <td className="numero">{moneda(p.ultimoPrecio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 18 }}>
            <button className="enlace" onClick={() => setVerMovimientos((v) => !v)}>
              {verMovimientos ? 'Ocultar movimientos' : 'Ver movimientos de esta refacción'}
            </button>
          </div>

          {verMovimientos && (
            !movimientos ? <Cargando /> : movimientos.length === 0 ? (
              <div className="vacio">Sin movimientos registrados.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Motivo</th>
                    <th className="numero">Cantidad</th>
                    <th className="numero">Saldo</th>
                    <th>OT</th>
                    <th>Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => (
                    <tr key={m.id}>
                      <td>{fechaCorta(m.creadoEn)}</td>
                      <td>{m.tipo}</td>
                      <td>{ETIQUETAS_MOTIVO[m.motivo] ?? m.motivo}</td>
                      <td
                        className="numero"
                        style={{ color: m.cantidad < 0 ? '#b02a37' : '#1c7a4c' }}
                      >
                        {m.cantidad > 0 ? `+${m.cantidad}` : m.cantidad}
                      </td>
                      <td className="numero">{m.saldoResultante}</td>
                      <td className="mono">
                        {m.ordenTrabajo ? folioOT(m.ordenTrabajo.folio) : ''}
                      </td>
                      <td className="mono">{m.referenciaFolio ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </>
      )}
    </Dialogo>
  );
}

/** Una etiqueta con su valor. Nulo se ve como raya, no como hueco. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span>{valor ?? '—'}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════ movimientos

const ETIQUETAS_MOTIVO: Record<string, string> = {
  compra_proveedor: 'Compra a proveedor',
  devolucion_ot: 'Devolución de orden',
  transferencia_bodegas: 'Traspaso',
  ajuste_inventario: 'Ajuste por conteo',
  salida_ot: 'Salida a orden',
  inventario_inicial: 'Inventario inicial',
  otro: 'Otro',
};

function Movimientos({ bodegaId, recarga }: { bodegaId: string; recarga: number }) {
  const [tipo, setTipo] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<MovimientoInventario> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<MovimientoInventario>>('/almacen/movimientos', {
      bodegaId: bodegaId || undefined,
      tipo: tipo || undefined,
      pagina: numero,
      limite: 50,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [bodegaId, tipo, numero, recarga]);

  return (
    <div className="panel">
      <div className="controles">
        <select value={tipo} onChange={(e) => { setTipo(e.target.value); setNumero(1); }}>
          <option value="">Todos los movimientos</option>
          <option value="entrada">Entradas</option>
          <option value="salida">Salidas</option>
          <option value="ajuste">Ajustes</option>
        </select>
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">Todavía no hay movimientos.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Refacción</th>
                  <th>Tipo</th>
                  <th>Motivo</th>
                  <th className="numero">Cantidad</th>
                  <th className="numero">Saldo</th>
                  <th>OT</th>
                  <th>Referencia</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((m) => (
                  <tr key={m.id}>
                    <td>{fechaCorta(m.creadoEn)}</td>
                    <td>
                      <span className="mono">{m.refaccion.codigo}</span> {m.refaccion.nombre}
                    </td>
                    <td>{m.tipo}</td>
                    <td>{ETIQUETAS_MOTIVO[m.motivo] ?? m.motivo}</td>
                    <td className="numero" style={{ color: m.cantidad < 0 ? '#b02a37' : '#1c7a4c' }}>
                      {m.cantidad > 0 ? `+${m.cantidad}` : m.cantidad}
                    </td>
                    <td className="numero">{m.saldoResultante}</td>
                    {/* En blanco y no con guion: el guion dice «se buscó
                        y no hay», y aquí lo que pasa es que ese
                        movimiento no es de ninguna orden. */}
                    <td className="mono">
                      {m.ordenTrabajo ? folioOT(m.ordenTrabajo.folio) : ''}
                    </td>
                    <td className="mono">{m.referenciaFolio ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina}
            limite={pagina.limite}
            total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}
    </div>
  );
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// ══════════════════════════════════════════════════════════ formulario

function FormularioMovimiento({
  operacion, bodegas, fallaBodegas, bodegaPreferida, onCerrar, onHecho,
}: {
  operacion: 'entrada' | 'salida' | 'ajuste';
  bodegas: Opcion[];
  fallaBodegas?: string;
  bodegaPreferida: string;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [refaccion, setRefaccion] = useState<Refaccion | null>(null);
  const [bodegaId, setBodegaId] = useState(bodegaPreferida || bodegas[0]?.id || '');
  const [cantidad, setCantidad] = useState('');
  const [costoUnitario, setCostoUnitario] = useState('');
  const [motivo, setMotivo] = useState(
    operacion === 'entrada' ? 'compra_proveedor' : operacion === 'salida' ? 'salida_ot' : 'ajuste_inventario',
  );
  const [ordenBusqueda, setOrdenBusqueda] = useState('');
  const [ordenes, setOrdenes] = useState<{ id: string; folio: string; sucursal: string }[]>([]);
  const [ordenTrabajoId, setOrdenTrabajoId] = useState('');
  const [referenciaFolio, setReferenciaFolio] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    if (!bodegaPreferida && bodegas.length > 0) setBodegaId(bodegas[0].id);
  }, [bodegas, bodegaPreferida]);

  useEffect(() => {
    if (operacion !== 'salida' || motivo !== 'salida_ot') return;
    const t = setTimeout(() => {
      api.get<Pagina<{ id: string; folio: string; sucursal: { nombre: string } | null }>>(
        '/ordenes',
        { busqueda: ordenBusqueda || undefined, bandeja: 'abiertas', limite: 15 },
      )
        .then((r) => setOrdenes(r.data.map((o) => ({
          id: o.id, folio: o.folio, sucursal: o.sucursal?.nombre ?? '',
        }))))
        .catch(() => setOrdenes([]));
    }, ordenBusqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [ordenBusqueda, operacion, motivo]);

  const titulo = operacion === 'entrada' ? 'Registrar entrada'
    : operacion === 'salida' ? 'Registrar salida' : 'Ajustar por conteo';

  // Lo que impide guardar, dicho con palabras. Un botón gris y mudo
  // obliga a recorrer el formulario adivinando cuál campo falta.
  const faltantes: string[] = [];
  if (!refaccion) faltantes.push('elegir una refacción del catálogo');
  if (!bodegaId) faltantes.push('elegir bodega');
  if (cantidad === '') faltantes.push(operacion === 'ajuste' ? 'las piezas contadas' : 'la cantidad');
  if (operacion === 'salida' && motivo === 'salida_ot' && !ordenTrabajoId) {
    faltantes.push('elegir la orden de trabajo');
  }
  if (operacion === 'ajuste' && observaciones.trim().length < 10) {
    faltantes.push('la explicación del ajuste, de al menos 10 caracteres');
  }

  const listo = faltantes.length === 0;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      if (operacion === 'entrada') {
        await api.post('/almacen/entradas', {
          refaccionId: refaccion!.id, bodegaId,
          cantidad: Number(cantidad),
          costoUnitario: costoUnitario === '' ? undefined : Number(costoUnitario),
          motivo,
          referenciaFolio: referenciaFolio.trim() || undefined,
          observaciones: observaciones.trim() || undefined,
        });
      } else if (operacion === 'salida') {
        await api.post('/almacen/salidas', {
          refaccionId: refaccion!.id, bodegaId,
          cantidad: Number(cantidad),
          motivo,
          ordenTrabajoId: motivo === 'salida_ot' ? ordenTrabajoId : undefined,
          observaciones: observaciones.trim() || undefined,
        });
      } else {
        await api.post('/almacen/ajustes', {
          refaccionId: refaccion!.id, bodegaId,
          cantidadContada: Number(cantidad),
          observaciones: observaciones.trim(),
        });
      }
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : 'Registrar'}
          </button>
        </>
      }
    >
      <Error error={error} />
      <Faltantes que={faltantes} />

      {/* El buscador de siempre, en vez de un desplegable de resultados.
          El que estaba iba controlado en `refaccionId`, que arranca
          vacío, y no había ninguna opción vacía que le correspondiera:
          el navegador dejaba la primera marcada de entrada, así que
          hacer clic en ella no cambiaba nada y no disparaba onChange. La
          primera coincidencia era la única que no se podía escoger. */}
      <BuscadorRefaccion valor={refaccion} onElegir={setRefaccion} autoFoco />

      {/* La bodega se pide siempre que haya de dónde elegir. Ocultarla
          cuando sólo hay una parecía buena idea hasta que la carga falló:
          el campo desaparecía y el botón se quedaba gris sin motivo. */}
      {bodegas.length > 0 ? (
        <div className="campo">
          <label htmlFor="bodegaId">Bodega *</label>
          <select id="bodegaId" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)}>
            <option value="">Seleccione una opción</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
      ) : (
        <div className="campo">
          <label>Bodega *</label>
          <Falla mensaje={fallaBodegas ?? 'No hay bodegas activas en el catálogo.'} />
        </div>
      )}

      <div className="campo">
        <label htmlFor="cantidad">
          {operacion === 'ajuste' ? 'Piezas contadas *' : 'Cantidad *'}
        </label>
        <input
          id="cantidad" type="number" min={operacion === 'ajuste' ? 0 : 1} step={1}
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
        {operacion === 'ajuste' && (
          <span className="ayuda">
            Escribe lo que hay de verdad en el anaquel, no la diferencia.
            El sistema calcula el faltante o el sobrante.
          </span>
        )}
      </div>

      {operacion === 'entrada' && (
        <>
          <div className="campo">
            <label htmlFor="motivo">Motivo</label>
            <select id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              <option value="compra_proveedor">Compra a proveedor</option>
              <option value="devolucion_ot">Devolución de una orden</option>
              <option value="inventario_inicial">Inventario inicial</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div className="campo">
            <label htmlFor="costoUnitario">Costo unitario</label>
            <input
              id="costoUnitario" type="number" min={0} step="0.01" value={costoUnitario}
              onChange={(e) => setCostoUnitario(e.target.value)}
            />
            <span className="ayuda">
              Recalcula el costo promedio ponderado. Si lo dejas vacío, el
              promedio no se mueve — que es lo correcto en una devolución,
              porque la pieza ya se valuó al salir.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="referenciaFolio">Folio de referencia</label>
            <input
              id="referenciaFolio" className="mono" value={referenciaFolio} maxLength={30}
              placeholder="Factura, remisión u orden de compra"
              onChange={(e) => setReferenciaFolio(e.target.value)}
            />
          </div>
        </>
      )}

      {operacion === 'salida' && (
        <>
          <div className="campo">
            <label htmlFor="motivo">Motivo</label>
            <select id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              <option value="salida_ot">Salida a una orden</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          {motivo === 'salida_ot' && (
            <div className="campo">
              <label htmlFor="buscarOrden">Orden de trabajo *</label>
              <input
                id="buscarOrden"
                type="search"
                placeholder="Folio o sucursal…"
                value={ordenBusqueda}
                onChange={(e) => setOrdenBusqueda(e.target.value)}
              />
              <select
                value={ordenTrabajoId}
                size={4}
                onChange={(e) => setOrdenTrabajoId(e.target.value)}
              >
                {ordenes.map((o) => (
                  <option key={o.id} value={o.id}>{folioOT(o.folio)} — {o.sucursal}</option>
                ))}
              </select>
              <span className="ayuda">
                La salida le carga a la orden el valor a costo promedio. Es
                la única fuente del costo de refacciones.
              </span>
            </div>
          )}
        </>
      )}

      <div className="campo">
        <label htmlFor="observaciones">
          {operacion === 'ajuste' ? 'Explicación *' : 'Observaciones'}
        </label>
        <textarea
          id="observaciones" rows={3} value={observaciones}
          placeholder={operacion === 'ajuste'
            ? 'Por qué no cuadra: merma, error de captura, pieza dañada… Mínimo 10 caracteres.'
            : undefined}
          onChange={(e) => setObservaciones(e.target.value)}
        />
        {operacion === 'ajuste' && (
          <span className="ayuda">
            Un ajuste mueve existencias sin comprobante. Sin explicación, el
            faltante del mes que viene no tiene a qué atribuirse.
          </span>
        )}
      </div>
    </Dialogo>
  );
}

/**
 * Qué se le ha surtido a una tienda, y qué va en camino.
 *
 * Karin: «cada tienda en ocasiones se surte de refacciones a través de una
 * OT o solicitud por algún requerimiento, pero es importante para soporte
 * tener la manera de entrar y ver el histórico o generar un reporte de esa
 * sucursal de cuantas veces se le ha surtido, para no caer en que soliciten
 * una manguera 3 dias despues de haberlo hecho».
 *
 * El dato existía entero y nadie lo veía: la salida vive en el movimiento
 * de inventario, lo pedido en la solicitud a almacén y lo comprado en la
 * orden de compra, y la tienda sólo aparece dando un rodeo por la orden.
 *
 * Empieza por la tienda y no por una lista: la pregunta siempre es sobre
 * una sucursal concreta, y una tabla con todo de todas no se lee.
 */
function SurtidoPorTienda() {
  const [busqueda, setBusqueda] = useState('');
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [elegida, setElegida] = useState<Sucursal | null>(null);
  const [refaccion, setRefaccion] = useState<Refaccion | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [datos, setDatos] = useState<SurtidoDeLaTienda | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);

  /**
   * El archivo lleva los mismos filtros que la pantalla.
   *
   * Si la descarga trajera otra cosa que lo que se está viendo, el que se
   * lleva a la conversación con la tienda es el archivo, y sería el
   * equivocado.
   */
  async function descargar() {
    if (!elegida) return;

    setError(null);
    setDescargando(true);
    try {
      await api.descargar('/almacen/surtido/reporte', {
        sucursalId: elegida.id,
        refaccionId: refaccion?.id || undefined,
        desde: desde ? new Date(desde).toISOString() : undefined,
        hasta: hasta ? new Date(hasta).toISOString() : undefined,
      }, `surtido-${elegida.determinante}.xlsx`);
    } catch (e) {
      setError(e);
    } finally {
      setDescargando(false);
    }
  }

  useEffect(() => {
    if (busqueda.trim().length < 2) { setSucursales([]); return undefined; }

    const t = setTimeout(() => {
      api.get<Pagina<Sucursal>>('/sucursales', {
        busqueda: busqueda.trim(), estado: 'activo', limite: 20,
      })
        .then((r) => setSucursales(r.data))
        .catch(setError);
    }, 300);

    return () => clearTimeout(t);
  }, [busqueda]);

  const cargar = useCallback(() => {
    if (!elegida) { setDatos(null); return; }

    setCargando(true);
    setError(null);

    api.get<SurtidoDeLaTienda>('/almacen/surtido', {
      sucursalId: elegida.id,
      refaccionId: refaccion?.id || undefined,
      desde: desde ? new Date(desde).toISOString() : undefined,
      hasta: hasta ? new Date(hasta).toISOString() : undefined,
    })
      .then(setDatos)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [elegida, refaccion, desde, hasta]);

  useEffect(cargar, [cargar]);

  return (
    <>
      <Error error={error} />

      <div className="filtros">
        <div className="campo">
          <label htmlFor="surtido-tienda">Tienda *</label>
          {elegida ? (
            <div className="controles">
              <strong>{elegida.determinante} — {elegida.nombre}</strong>
              <button className="enlace" onClick={() => { setElegida(null); setDatos(null); }}>
                cambiar
              </button>
            </div>
          ) : (
            <input
              id="surtido-tienda" value={busqueda}
              placeholder="Determinante o nombre de la sucursal"
              onChange={(e) => setBusqueda(e.target.value)}
            />
          )}
        </div>

        {!elegida && sucursales.length > 0 && (
          <ul className="sugerencias">
            {sucursales.map((su) => (
              <li key={su.id}>
                <button className="enlace" onClick={() => { setElegida(su); setBusqueda(''); }}>
                  {su.determinante} — {su.nombre}
                </button>
              </li>
            ))}
          </ul>
        )}

        {elegida && (
          <>
            <div className="campo">
              <span className="etiqueta">Una pieza en concreto</span>
              <BuscadorRefaccion
                valor={refaccion} onElegir={setRefaccion} id="surtido-refaccion"
              />
              <span className="ayuda">
                Vacío trae todo lo que se le ha surtido a la tienda.
              </span>
            </div>

            <div className="campo">
              <label htmlFor="surtido-desde">Desde</label>
              <input
                id="surtido-desde" type="date" value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>

            <div className="campo">
              <label htmlFor="surtido-hasta">Hasta</label>
              <input
                id="surtido-hasta" type="date" value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
              <span className="ayuda">Vacíos: los últimos seis meses.</span>
            </div>
          </>
        )}
      </div>

      {!elegida && (
        <p className="sub">
          Elige una tienda para ver qué se le ha surtido. Es lo que evita
          pedirle una manguera tres días después de haberle mandado otra.
        </p>
      )}

      {cargando && <Cargando />}

      {elegida && datos && !cargando && (
        <>
          <div className="controles" style={{ marginBottom: 10 }}>
            <button onClick={() => { void descargar(); }} disabled={descargando}>
              {descargando ? 'Generando…' : 'Exportar a Excel'}
            </button>
          </div>

          {/* Primero el resumen: una tienda con nueve mangueras en tres
              meses no se nota leyendo nueve renglones sueltos, se nota en
              un renglón que dice nueve. */}
          <h2>Cuántas veces</h2>
          {datos.porRefaccion.length === 0 ? (
            <p className="sub">
              No se le ha surtido nada en este periodo.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Refacción</th>
                  <th className="numero">Veces</th>
                  <th className="numero">Piezas</th>
                  <th>La última</th>
                </tr>
              </thead>
              <tbody>
                {datos.porRefaccion.map((r) => (
                  <tr key={r.refaccionId ?? r.descripcion}>
                    <td>{r.descripcion}</td>
                    <td className="numero">{r.veces}</td>
                    <td className="numero">{r.piezas}</td>
                    <td>{fechaCorta(r.ultima)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {datos.renglones.length > 0 && (
            <>
              <h2>Movimiento por movimiento</h2>
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Refacción</th>
                    <th className="numero">Cant.</th>
                    <th>En qué va</th>
                    <th>Orden</th>
                    <th>Documento</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.renglones.map((r, i) => (
                    <tr key={`${r.fecha}-${r.documento ?? i}-${r.descripcion}`}>
                      <td>{fechaCorta(r.fecha)}</td>
                      <td>{r.descripcion}</td>
                      <td className="numero">{r.cantidad}</td>
                      <td>
                        <span className={`etiqueta ${r.situacion === 'surtida' ? 'si' : 'no'}`}>
                          {SITUACION_SURTIDO[r.situacion]}
                        </span>
                      </td>
                      <td className="mono">{r.orden ?? '—'}</td>
                      <td className="mono">{r.documento ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </>
  );
}

/** Cómo se lee cada situación. Las claves van entrecomilladas. */
const SITUACION_SURTIDO: Record<string, string> = {
  'surtida': 'Ya se le surtió',
  'en_camino': 'Comprada, no llega',
  'solicitada': 'Pedida a almacén',
};
